/**
 * NastaliqMetrics - Character-specific metrics for Nastaliq calligraphy
 *
 * Nastaliq script has unique characteristics:
 * - Sarkesh (decorative marks) extend far above baseline on letters like ک، گ، ی
 * - Dots (nuqta) can appear above or below letters
 * - Letters change shape based on position in word (isolated, initial, medial, final)
 * - Ligatures combine multiple letters into connected forms
 */

/**
 * Characters with tall sarkesh strokes
 * These can extend 2-3x the font size above baseline
 */
export const TALL_SARKESH_CHARS = new Set([
  'ک', // kaf
  'گ', // gaf
  'ی', // ye
  'ئ', // ye with hamza
  'ل', // lam (can have sarkesh in certain styles)
  'ب', // be (initial form can have tall stroke)
  'پ', // pe
  'ت', // te
  'ث', // se
  'ن', // nun
])

/**
 * Characters with dots below
 * These require extra descent padding
 */
export const DOTS_BELOW_CHARS = new Set([
  'ب', // be (1 dot below)
  'پ', // pe (3 dots below)
  'ج', // jim (1 dot below)
  'چ', // che (3 dots below)
  'خ', // khe (1 dot above)
  'ی', // ye (2 dots below in initial/medial)
])

/**
 * Characters with dots above
 */
export const DOTS_ABOVE_CHARS = new Set([
  'ت', // te (2 dots above)
  'ث', // se (3 dots above)
  'ج', // jim
  'خ', // khe
  'ذ', // zal
  'ز', // ze
  'ژ', // zhe
  'ش', // shin
  'ض', // zad
  'ظ', // za
  'غ', // ghain
  'ف', // fe
  'ق', // qaf
  'ن', // nun
])

/**
 * Persian connector words that should have tight spacing
 */
export const PERSIAN_CONNECTORS = new Set([
  'که', 'از', 'به', 'با', 'تا', 'در', 'را', 'و', 'یا', 'هم',
  'چه', 'کی', 'می', 'نه', 'ای', 'اگر', 'اما', 'ولی', 'پس',
  'زیر', 'روی', 'بر', 'تو', 'ما', 'او', 'آن', 'این',
])

/**
 * Calculate ascent multiplier for a word based on its characters
 * Returns a multiplier to apply to base ascent
 */
export function calculateAscentMultiplier(word: string): number {
  let maxMultiplier = 1.0

  for (const char of word) {
    if (TALL_SARKESH_CHARS.has(char)) {
      // Tall sarkesh characters need more space above
      maxMultiplier = Math.max(maxMultiplier, 2.5)
    } else if (DOTS_ABOVE_CHARS.has(char)) {
      // Dots above need moderate extra space
      maxMultiplier = Math.max(maxMultiplier, 1.8)
    }
  }

  return maxMultiplier
}

/**
 * Calculate descent multiplier for a word based on its characters
 * Returns a multiplier to apply to base descent
 */
export function calculateDescentMultiplier(word: string): number {
  let maxMultiplier = 1.0

  for (const char of word) {
    if (DOTS_BELOW_CHARS.has(char)) {
      // Dots below need extra space
      maxMultiplier = Math.max(maxMultiplier, 1.8)
    }
  }

  return maxMultiplier
}

/**
 * Calculate padding for a specific word
 * Returns { top, bottom } padding in multiples of fontSize
 */
export function calculateWordPadding(
  word: string,
  fontSize: number
): { top: number; bottom: number } {
  const ascentMultiplier = calculateAscentMultiplier(word)
  const descentMultiplier = calculateDescentMultiplier(word)

  return {
    top: fontSize * (ascentMultiplier - 1) * 0.5,
    bottom: fontSize * (descentMultiplier - 1) * 0.3,
  }
}

/**
 * Check if a character is a Persian digit
 */
export function isPersianDigit(char: string): boolean {
  const code = char.charCodeAt(0)
  return code >= 0x06F0 && code <= 0x06F9 // Persian digits ۰-۹
}

/**
 * Check if a character is an Arabic digit
 */
export function isArabicDigit(char: string): boolean {
  const code = char.charCodeAt(0)
  return code >= 0x0660 && code <= 0x0669 // Arabic-Indic digits ٠-٩
}

/**
 * Check if a character is a Latin digit
 */
export function isLatinDigit(char: string): boolean {
  const code = char.charCodeAt(0)
  return code >= 0x0030 && code <= 0x0039 // ASCII digits 0-9
}

/**
 * Check if a word contains mixed LTR/RTL content
 * This is common when Persian text contains numbers
 */
export function hasMixedDirection(word: string): boolean {
  let hasRTL = false
  let hasLTR = false

  for (const char of word) {
    const code = char.charCodeAt(0)

    // Persian/Arabic characters (RTL)
    if (
      (code >= 0x0600 && code <= 0x06FF) || // Arabic
      (code >= 0xFB50 && code <= 0xFDFF) || // Arabic Presentation Forms-A
      (code >= 0xFE70 && code <= 0xFEFF)    // Arabic Presentation Forms-B
    ) {
      hasRTL = true
    }

    // Latin characters and digits (LTR in mixed context)
    if (
      (code >= 0x0041 && code <= 0x005A) || // A-Z
      (code >= 0x0061 && code <= 0x007A) || // a-z
      (code >= 0x0030 && code <= 0x0039)    // 0-9
    ) {
      hasLTR = true
    }

    if (hasRTL && hasLTR) return true
  }

  return false
}

/**
 * Segment text into runs of consistent direction
 * Used for proper rendering of mixed LTR/RTL text
 */
export interface TextRun {
  text: string
  direction: 'rtl' | 'ltr'
  startIndex: number
}

export function segmentByDirection(text: string): TextRun[] {
  const runs: TextRun[] = []
  let currentRun = ''
  let currentDir: 'rtl' | 'ltr' = 'rtl'
  let startIndex = 0

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const code = char.charCodeAt(0)

    // Determine character direction
    let charDir: 'rtl' | 'ltr' | 'neutral' = 'neutral'

    if (
      (code >= 0x0600 && code <= 0x06FF) ||
      (code >= 0xFB50 && code <= 0xFDFF) ||
      (code >= 0xFE70 && code <= 0xFEFF)
    ) {
      charDir = 'rtl'
    } else if (
      (code >= 0x0041 && code <= 0x005A) ||
      (code >= 0x0061 && code <= 0x007A) ||
      (code >= 0x0030 && code <= 0x0039)
    ) {
      charDir = 'ltr'
    }

    // Neutral characters inherit direction
    if (charDir === 'neutral') {
      currentRun += char
      continue
    }

    // Direction change
    if (charDir !== currentDir && currentRun.length > 0) {
      runs.push({ text: currentRun, direction: currentDir, startIndex })
      currentRun = ''
      startIndex = i
    }

    currentDir = charDir
    currentRun += char
  }

  // Push final run
  if (currentRun.length > 0) {
    runs.push({ text: currentRun, direction: currentDir, startIndex })
  }

  return runs
}

/**
 * Get recommended bounds expansion for a word
 * Returns { horizontal, vertical } expansion in pixels
 */
export function getRecommendedExpansion(
  word: string,
  fontSize: number
): { horizontal: number; verticalAbove: number; verticalBelow: number } {
  const padding = calculateWordPadding(word, fontSize)

  return {
    horizontal: fontSize * 0.3, // 30% for connecting strokes
    verticalAbove: fontSize * 2.0 + padding.top, // Base + sarkesh padding
    verticalBelow: fontSize * 1.5 + padding.bottom, // Base + dots padding
  }
}
